import { Typography } from "@mui/material";
import {
    ConversationSearch,
    MIN_SEARCH_LENGTH,
} from "components/UI/ConversationSearch/ConversationSearch";
import { Duration } from "components/UI/Duration";
import { TranscriptBySpeaker } from "models/LLMs/Testing/LLMExecutorExecutionResult";
import moment from "moment";
import React from "react";
import { HighlightedText, splitOnMatches } from "./HighlightedText";

export type TranscriptSearchResult = {
    index: number;
    matchStart: number;
    transcriptMoment: TranscriptBySpeaker;
};

export function getTranscriptSearchResults(
    transcript: TranscriptBySpeaker[] | undefined,
    searchString: string,
): TranscriptSearchResult[] {
    if (!transcript || searchString.length < MIN_SEARCH_LENGTH) return [];

    // Use the same offsets as the rendered highlights so every result has its
    // own exact scroll target, including repeated matches within one turn.
    return transcript.flatMap((transcriptMoment, index) =>
        splitOnMatches(transcriptMoment.transcriptionText, searchString)
            .filter((segment) => segment.isMatch)
            .map((segment) => ({
                transcriptMoment,
                index,
                matchStart: segment.start,
            })),
    );
}

export const SNIPPET_CONTEXT_BEFORE = 60;
export const SNIPPET_MAX_LENGTH = 220;

/**
 * Moves a cut forward to the start of the next whole word, so a snippet never
 * opens mid-word. Never advances past `limit`, which keeps the match itself
 * inside the window.
 */
function snapStartToWord(text: string, start: number, limit: number): number {
    if (start <= 0) return 0;

    const nextSpace = text.indexOf(" ", start);
    if (nextSpace === -1 || nextSpace >= limit) return start;

    return nextSpace + 1;
}

/**
 * Moves a cut back to the end of the previous whole word. Never retreats past
 * `limit`, for the same reason.
 */
function snapEndToWord(text: string, end: number, limit: number): number {
    if (end >= text.length) return text.length;

    const previousSpace = text.lastIndexOf(" ", end);
    if (previousSpace === -1 || previousSpace <= limit) return end;

    return previousSpace;
}

export function buildSnippet(
    text: string,
    matchIndex: number,
    matchLength: number,
) {
    if (text.length <= SNIPPET_MAX_LENGTH)
        return { text, matchStart: matchIndex };

    const matchEnd = matchIndex + matchLength;
    const start = snapStartToWord(
        text,
        Math.max(0, matchIndex - SNIPPET_CONTEXT_BEFORE),
        matchIndex,
    );
    const end = snapEndToWord(
        text,
        Math.min(text.length, Math.max(matchEnd, start + SNIPPET_MAX_LENGTH)),
        matchEnd,
    );

    const prefix = start > 0 ? "\u2026" : "";
    return {
        text: `${prefix}${text.slice(start, end)}${
            end < text.length ? "\u2026" : ""
        }`,
        matchStart: prefix.length + matchIndex - start,
    };
}

type TranscriptSearchProps = {
    transcript: TranscriptBySpeaker[];
    searchString: string;
    onSearchStringChange: (searchString: string) => void;
    speakerToColor: Record<string, string>;
    onResultClick: (result: TranscriptSearchResult) => void;
};

export function TranscriptSearch({
    transcript,
    searchString,
    onSearchStringChange,
    speakerToColor,
    onResultClick,
}: TranscriptSearchProps) {
    const searchResults = getTranscriptSearchResults(transcript, searchString);

    return (
        <ConversationSearch
            inputWidth="100%"
            fitResultsToInput
            searchString={searchString}
            onSearchStringChange={onSearchStringChange}
            results={searchResults.map((result) => {
                const { transcriptMoment, matchStart, index } = result;
                const snippet = buildSnippet(
                    transcriptMoment.transcriptionText,
                    matchStart,
                    searchString.length,
                );
                return {
                    key: `${index}:${matchStart}`,
                    speaker: transcriptMoment.speaker,
                    speakerColor: speakerToColor[transcriptMoment.speaker],
                    timestamp: (
                        <>
                            {transcriptMoment.startTime != null && (
                                <Duration
                                    seconds={transcriptMoment.startTime}
                                />
                            )}
                            {transcriptMoment.timestamp &&
                                moment
                                    .utc(transcriptMoment.timestamp)
                                    .local()
                                    .format("HH:mm")}
                        </>
                    ),
                    content: (
                        <Typography>
                            <HighlightedText
                                text={snippet.text}
                                query={searchString}
                                onlyMatchStart={snippet.matchStart}
                            />
                        </Typography>
                    ),
                    onSelect: () => onResultClick(result),
                };
            })}
        />
    );
}
